import React, { useState, useEffect, useCallback } from 'react';
import { Users, Tag, Search, ChevronDown, ChevronUp, ShoppingBag, X, ArrowLeft, SlidersHorizontal } from 'lucide-react';
import client from '../../api/client';
import { formatDate } from '../../utils/date';
import { TAG_CATEGORIES, sortTags, getTagCategory } from '../../constants/tags';
import { toast } from '../../components/Toast/toast';
import { formatPrice } from '../../utils/price';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState(null);
  const [filterRole, setFilterRole] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('last_active');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTags, setUserTags] = useState([]);
  const [tagHistory, setTagHistory] = useState([]);
  const [tagTab, setTagTab] = useState('status');
  const [showTagHistory, setShowTagHistory] = useState(false);
  const [copied, setCopied] = useState(null);

  const copyValue = async (field, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      return;
    }
    setCopied(field);
    setTimeout(() => setCopied(null), 1500);
  };

  const getTagCategoryLabel = (slug) => {
    const cat = getTagCategory(slug);
    return TAG_CATEGORIES[cat]?.label || '';
  };

  const filteredTags = tags.filter(t => getTagCategory(t.slug) === tagTab);

  const load = useCallback(async () => {
    try {
      const [usersRes, tagsRes] = await Promise.all([
        client.get('/admin/users'),
        client.get('/admin/tags'),
      ]);
      setUsers(usersRes.data || []);
      setTags(sortTags(tagsRes.data || []));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Фильтрация и сортировка
  const filtered = users
    .filter(u => {
      if (search) {
        const q = search.toLowerCase();
        const name = (u.first_name || '').toLowerCase();
        const uname = (u.username || '').toLowerCase();
        if (!name.includes(q) && !uname.includes(q)) return false;
      }
      if (filterTag && !u.tags?.some(t => t.id === filterTag)) return false;
      if (filterRole === 'manager' && u.role !== 'manager') return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'orders') return b.orders_count - a.orders_count;
      if (sortBy === 'spent') return b.total_spent - a.total_spent;
      if (sortBy === 'name') return (a.first_name || '').localeCompare(b.first_name || '');
      return new Date(b.last_active || 0) - new Date(a.last_active || 0);
    });

  const openUser = async (user) => {
    setSelectedUser(user);
    setUserTags(user.tags?.map(t => t.id) || []);
    try {
      const res = await client.get(`/admin/users/${user.id}/tag-history`);
      setTagHistory(res.data || []);
    } catch {
      setTagHistory([]);
    }
  };

  const toggleUserTag = async (tagId) => {
    const newTags = userTags.includes(tagId) ? userTags.filter(id => id !== tagId) : [...userTags, tagId];
    setUserTags(newTags);
    try {
      await client.put(`/admin/users/${selectedUser.id}/tags`, { tag_ids: newTags });
      // Перезагружаем пользователя с сервера чтобы получить is_manual
      const usersRes = await client.get('/admin/users');
      setUsers(usersRes.data || []);
      const updatedUser = (usersRes.data || []).find(u => u.id === selectedUser.id);
      if (updatedUser) setSelectedUser(updatedUser);
    } catch {
      toast.error('Ошибка сохранения тега');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#6B9B74]"></div>
      </div>
    );
  }

  // Детали клиента
  if (selectedUser) {
    return (
      <div className="space-y-4 animate-fade-in">
        {/* Назад */}
        <button
          onClick={() => { setSelectedUser(null); setTagTab('status'); }}
          className="flex items-center gap-2 text-sm text-[#374151] font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          Назад к списку
        </button>

        {/* Профиль */}
        <div className="rounded-2xl bg-white ring-1 ring-[#e2e8f0] overflow-hidden">
          {/* Баннер роли */}
          {selectedUser.role === 'owner' && (
            <div className="bg-[#374151] px-4 py-1.5 text-center text-xs font-medium text-white">Владелец</div>
          )}
          {selectedUser.role === 'developer' && (
            <div className="bg-[#4A6FA5] px-4 py-1.5 text-center text-xs font-medium text-white">Разработчик</div>
          )}
          {selectedUser.role === 'manager' && (
            <div className="bg-[#0D9488] px-4 py-1.5 text-center text-xs font-medium text-white">Менеджер</div>
          )}
          {(!selectedUser.role || selectedUser.role === 'user') && (
            <div className="bg-[#e2e8f0] px-4 py-1.5 text-center text-xs font-medium text-[#64748b]">Клиент</div>
          )}
          <div className="p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-[#374151]/15 text-[#374151] text-lg font-bold">
              {(selectedUser.first_name || selectedUser.username || '?')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-[#0f172a]">
                {selectedUser.first_name || (selectedUser.username ? `@${selectedUser.username}` : null) || `ID ${selectedUser.telegram_id || selectedUser.max_id || '?'}`}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] text-[#64748b]">Активность</p>
              <p className="text-xs font-medium text-[#0f172a]">{formatDate(selectedUser.last_active)}</p>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="w-[35%] rounded-xl bg-[#f8fafc] px-3 py-2 text-center">
              <p className="text-lg font-bold text-[#4A6FA5]">{selectedUser.orders_count}</p>
              <p className="text-[10px] text-[#64748b]">Заказов</p>
            </div>
            <div className="flex-1 rounded-xl bg-[#f8fafc] px-3 py-2 text-center">
              <p className="text-lg font-bold text-[#6B9B74]">{formatPrice(Math.round(selectedUser.total_spent))} ₽</p>
              <p className="text-[10px] text-[#64748b]">Потратил</p>
            </div>
          </div>
          </div>
        </div>

        {/* Теги */}
        <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
          <h3 className="text-sm font-semibold text-[#0f172a] mb-3">Теги</h3>
          {/* Вкладки категорий */}
          <div className="flex gap-1 pb-3 mb-3 border-b border-[#f1f5f9]">
            {Object.entries(TAG_CATEGORIES).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => setTagTab(key)}
                className={`rounded-xl border px-3 py-1.5 text-xs font-medium transition ${
                  tagTab === key
                    ? 'border-[#374151] bg-[#374151]/10 text-[#374151]'
                    : 'border-[#e2e8f0] bg-white text-[#64748b]'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
          {/* Теги выбранной категории */}
          <div className="flex flex-wrap gap-1.5">
            {filteredTags.map(tag => {
              const isActive = userTags.includes(tag.id);
              const assignedTag = selectedUser.tags?.find(t => t.id === tag.id);
              const isManual = assignedTag?.is_manual;
              return (
                <button
                  key={tag.id}
                  onClick={() => toggleUserTag(tag.id)}
                  className={`rounded-xl border px-2.5 py-1 text-[11px] font-medium transition ${
                    isActive
                      ? isManual
                        ? 'border-[#F59E0B] bg-[#F59E0B]/15 text-[#F59E0B]'
                        : 'border-[#3B82F6] bg-[#3B82F6]/15 text-[#3B82F6]'
                      : 'border-[#e2e8f0] bg-white text-[#64748b]'
                  }`}
                  title={isActive ? (isManual ? 'Назначен вручную' : 'Назначен автоматически') : undefined}
                >
                  {tag.name}
                </button>
              );
            })}
            {filteredTags.length === 0 && (
              <p className="text-xs text-[#64748b]">Нет тегов в этой категории</p>
            )}
          </div>
          {/* Легенда */}
          <div className="flex items-center gap-3 mt-3 pt-2 border-t border-[#f1f5f9]">
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-full bg-[#3B82F6]" />
              <span className="text-[10px] text-[#64748b]">Авто</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-full bg-[#F59E0B]" />
              <span className="text-[10px] text-[#64748b]">Вручную</span>
            </div>
          </div>
        </div>

        {/* История тегов */}
        <div className="rounded-2xl bg-white ring-1 ring-[#e2e8f0]">
          <div className="p-4 pb-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-[#0f172a]">История тегов</h3>
              {tagHistory.length > 1 && (
                <button
                  onClick={() => setShowTagHistory(!showTagHistory)}
                  className="flex items-center gap-1 text-xs font-medium text-[#374151]"
                >
                  {showTagHistory ? 'Свернуть' : `Показать всё (${tagHistory.length})`}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showTagHistory ? 'rotate-180' : ''}`} />
                </button>
              )}
            </div>
          </div>
          <div className="px-4 pb-4">
            {tagHistory.length === 0 ? (
              <p className="text-xs text-[#64748b]">История пуста</p>
            ) : (
              <div className="space-y-2">
                {(showTagHistory ? tagHistory : tagHistory.slice(0, 1)).map(h => (
                  <div key={h.id} className="flex items-center justify-between text-xs py-1 border-b border-[#f1f5f9] last:border-0">
                    <div className="flex items-center gap-2">
                      <span>{h.action === 'added' ? '✅' : '❌'}</span>
                      <span className="font-medium text-[#0f172a]">{h.tag_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                        h.reason === 'manual' ? 'bg-[#F59E0B]/15 text-[#F59E0B]' : 'bg-[#3B82F6]/15 text-[#3B82F6]'
                      }`}>
                        {h.reason === 'auto' ? 'Авто' : h.reason === 'manual' ? 'Вручную' : h.reason === 'auto_expired' ? 'Истёк' : 'Повышение'}
                      </span>
                      <span className="text-[#64748b]">{new Date(h.created_at).toLocaleString('ru-RU')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Инфо */}
        <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
          <h3 className="text-sm font-semibold text-[#0f172a] mb-3">Информация</h3>
          <div className="space-y-2">
            <button
              onClick={() => copyValue('id', selectedUser.id)}
              className="flex w-full items-start justify-between gap-3 text-xs text-left"
            >
              <span className="shrink-0 text-[#64748b]">UUID</span>
              <span className="break-all text-right font-medium text-[#0f172a]">
                {copied === 'id' ? 'Скопировано' : (selectedUser.id || '—')}
              </span>
            </button>
            <button
              onClick={() => copyValue('phone', selectedUser.phone)}
              className="flex w-full items-start justify-between gap-3 text-xs text-left"
            >
              <span className="shrink-0 text-[#64748b]">Телефон</span>
              <span className="break-all text-right font-medium text-[#0f172a]">
                {copied === 'phone' ? 'Скопировано' : (selectedUser.phone || '—')}
              </span>
            </button>
            <div className="flex items-start justify-between gap-3 text-xs">
              <span className="shrink-0 text-[#64748b]">ID (telegram/max)</span>
              <span className="break-all text-right font-medium text-[#0f172a]">
                {`${selectedUser.telegram_id || '—'} / ${selectedUser.max_id || '—'}`}
              </span>
            </div>
            <div className="flex items-start justify-between gap-3 text-xs">
              <span className="shrink-0 text-[#64748b]">Адрес</span>
              <span className="break-all text-right font-medium text-[#0f172a]">
                {selectedUser.address || '—'}
              </span>
            </div>
            <div className="flex items-start justify-between gap-3 text-xs">
              <span className="shrink-0 text-[#64748b]">Дата регистрации</span>
              <span className="text-right font-medium text-[#0f172a]">
                {formatDate(selectedUser.created_at)}
              </span>
            </div>
            <div className="flex items-start justify-between gap-3 text-xs">
              <span className="shrink-0 text-[#64748b]">Первый заказ</span>
              <span className="text-right font-medium text-[#0f172a]">
                {formatDate(selectedUser.first_order_date)}
              </span>
            </div>
            <div className="flex items-start justify-between gap-3 text-xs">
              <span className="shrink-0 text-[#64748b]">Последняя активность</span>
              <span className="text-right font-medium text-[#0f172a]">
                {formatDate(selectedUser.last_active)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Поиск */}
      <div className="rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0]">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по имени или username..."
              className="w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc] pl-9 pr-9 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#374151] focus:ring-1 focus:ring-[#374151]/30"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="h-4 w-4 text-[#64748b]" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl border transition ${
              showFilters
                ? 'border-[#374151] bg-[#374151]/10 text-[#374151]'
                : 'border-[#e2e8f0] bg-white text-[#64748b] hover:bg-[#f8fafc]'
            }`}
          >
            <SlidersHorizontal className="h-4 w-4" />
          </button>
        </div>

        {/* Свёрнутые фильтры */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-[#e2e8f0] space-y-3">
            {/* Все / Менеджеры */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => { setFilterRole(null); }}
                className={`rounded-xl border px-3 py-1.5 text-xs font-medium transition ${
                  !filterRole
                    ? 'border-[#374151] bg-[#374151]/10 text-[#374151]'
                    : 'border-[#e2e8f0] bg-white text-[#64748b]'
                }`}
              >
                Все
              </button>
              <button
                onClick={() => {
                  if (filterRole === 'manager') {
                    setFilterRole(null);
                  } else {
                    setFilterRole('manager');
                    setFilterTag(null);
                  }
                }}
                className={`rounded-xl border px-3 py-1.5 text-xs font-medium transition ${
                  filterRole === 'manager'
                    ? 'border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]'
                    : 'border-[#e2e8f0] bg-white text-[#64748b]'
                }`}
              >
                Менеджеры
              </button>
            </div>

            {/* Теги (скрыты при фильтре Менеджеры) */}
            {filterRole !== 'manager' && tags.length > 0 && (
              <div className="space-y-2">
                {Object.entries(TAG_CATEGORIES).map(([catKey, cat]) => (
                  <div key={catKey} className="space-y-1">
                    <span className="text-[10px] text-[#94a3b8]">{cat.label}:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.filter(t => getTagCategory(t.slug) === catKey).map(tag => (
                        <button
                          key={tag.id}
                          onClick={() => setFilterTag(filterTag === tag.id ? null : tag.id)}
                          className={`rounded-xl border px-2.5 py-1 text-[11px] font-medium transition ${
                            filterTag === tag.id
                              ? 'border-[#374151] bg-[#374151]/10 text-[#374151]'
                              : 'border-[#e2e8f0] bg-white text-[#64748b]'
                          }`}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Сортировка */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#64748b]">Сортировка:</span>
              {[
                { key: 'last_active', label: 'Активность' },
                { key: 'orders', label: 'Заказы' },
                { key: 'spent', label: 'Сумма' },
                { key: 'name', label: 'Имя' },
              ].map(s => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key)}
                  className={`rounded-lg px-2 py-1 text-[10px] font-medium transition ${
                    sortBy === s.key
                      ? 'bg-[#374151]/10 text-[#374151]'
                      : 'text-[#64748b] hover:bg-[#f8fafc]'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Счётчик */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs text-[#64748b]">{filtered.length} из {users.length}</span>
      </div>

      {/* Список клиентов */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl bg-white p-8 ring-1 ring-[#e2e8f0] text-center">
          <Users className="h-8 w-8 text-[#64748b]/30 mx-auto mb-2" />
          <p className="text-sm text-[#64748b]">Клиенты не найдены</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0">
          {filtered.map(u => (
            <button
              key={u.id}
              onClick={() => openUser(u)}
              className="w-full flex items-center gap-3 rounded-2xl bg-white p-4 ring-1 ring-[#e2e8f0] transition hover:bg-[#f8fafc] active:scale-[0.99] text-left"
            >
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#374151]/15 text-[#374151] text-sm font-bold">
                {(u.first_name || u.username || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#0f172a] truncate">
                    {u.first_name || (u.username ? `@${u.username}` : null) || `ID ${u.telegram_id || u.max_id || '?'}`}
                  </span>
                  {u.role === 'owner' && (
                    <span className="rounded-xl bg-[#374151]/10 px-1.5 py-0.5 text-[9px] font-medium text-[#374151]">владелец</span>
                  )}
                  {u.role === 'developer' && (
                    <span className="rounded-xl bg-[#4A6FA5]/10 px-1.5 py-0.5 text-[9px] font-medium text-[#4A6FA5]">разработчик</span>
                  )}
                  {u.role === 'manager' && (
                    <span className="rounded-xl bg-[#0D9488]/10 px-1.5 py-0.5 text-[9px] font-medium text-[#0D9488]">менеджер</span>
                  )}
                </div>
                {u.id && <span className="text-[11px] text-[#64748b]">{u.id.slice(0, 4)}...{u.id.slice(-4)}</span>}
                {u.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {sortTags(u.tags).map(t => (
                      <span
                        key={t.id}
                        className={`rounded-lg px-1.5 py-0.5 text-[9px] ${
                          t.is_manual
                            ? 'bg-[#F59E0B]/15 text-[#F59E0B]'
                            : 'bg-[#3B82F6]/15 text-[#3B82F6]'
                        }`}
                        title={t.is_manual ? 'Назначен вручную' : 'Назначен автоматически'}
                      >
                        {t.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-[#0f172a]">{u.orders_count}</p>
                <p className="text-[10px] text-[#64748b]">заказов</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
